import { ChakraProvider } from "@chakra-ui/react";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import NextProgress from "next-progress";
import { NextSeo } from "next-seo";
import type { AppProps } from "next/app";
import dynamic from "next/dynamic";
import { Inter } from "next/font/google";
import Head from "next/head";
import { NuqsAdapter } from "nuqs/adapters/next/pages";

import type { Session } from "@chainpatrol/auth";
import { SessionProvider } from "@chainpatrol/auth/react";
import { FeatureFlagProvider } from "@chainpatrol/feature-flags/react";

import { ErrorBoundary } from "~/components/ErrorBoundary";
import { RootErrorFallback } from "~/components/ErrorBoundary/RootErrorFallback";
import { theme } from "~/lib/theme";
import { trpc } from "~/lib/trpc";
import type { NextPageWithLayout } from "~/types/NextPageWithLayout";

const inter = Inter({ subsets: ["latin-ext"] });

type AppPropsWithLayout = AppProps<{
  session: Session;
}> & {
  Component: NextPageWithLayout<any>;
};

const AnalyticsIdentify = dynamic(() =>
  import("~/components/Analytics/AnalyticsIdentify").then((mod) => mod.AnalyticsIdentify),
);

function CustomApp({ Component, pageProps }: AppPropsWithLayout) {
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <>
      <style jsx global>
        {`
          :root {
            --font-inter: ${inter.style.fontFamily};
          }
        `}
      </style>
      <Head>
        <link rel="icon" href="/logo.svg" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="manifest" href="/site.webmanifest" />

        {/* https://api.slack.com/directory/using#suggestions */}
        <meta name="slack-app-id" content="A06DN9B2EE9" />
      </Head>

      <NextSeo titleTemplate={`%s | ChainPatrol`} defaultTitle="ChainPatrol" />

      <NextProgress
        delay={300}
        color="#c4b5fa"
        height="4px"
        options={{ showSpinner: false }}
      />

      <ChakraProvider theme={theme}>
        <SessionProvider
          session={pageProps.session}
          refetchInterval={2 * 60}
          refetchOnWindowFocus
          refetchWhenOffline={false}>
          <FeatureFlagProvider>
            <ErrorBoundary FallbackComponent={RootErrorFallback}>
              <NuqsAdapter>
                <VercelAnalytics />
                <AnalyticsIdentify />
                {getLayout(<Component {...pageProps} />)}
              </NuqsAdapter>
            </ErrorBoundary>
          </FeatureFlagProvider>
        </SessionProvider>
      </ChakraProvider>
    </>
  );
}

export default trpc.withTRPC(CustomApp);
